import React, { useEffect, useMemo, useState } from 'react';
import ProjectEstimatesService from '../../services/ProjectEstimatesService';
import ProjectService from '../../services/ProjectService';
import ExpenditureTypeService from '../../services/ExpenditureTypeService';
import MemberService from '../../services/MemberService';
import utility from '../../utils/utility';
import ReactPaginate from 'react-paginate';
import FilterBar from '../../components/FilterBar';
import { filterList } from '../../utils/filterUtils';

const ProjectEstimates = () => {
    const [estimates, setEstimates] = useState([]);
    const [projects, setProjects] = useState([]);
    const [expenditureTypes, setExpenditureTypes] = useState([]);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [pageCount, setPageCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;
    const [searchText, setSearchText] = useState('');
    const [filterProject, setFilterProject] = useState('');
    const [filterExpenditureType, setFilterExpenditureType] = useState('');
    const [filterMember, setFilterMember] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [formData, setFormData] = useState({
        id: '',
        projectId: '',
        expenditureTypeId: '',
        memberId: '',
        days: '',
        remunerationType: '',
        remunerationAmount: '',
        totalAmount: '',
        status: 'active'
    });
    const filteredEstimates = useMemo(() => estimates.filter((estimate) => {
        if (!searchText) return true;
        return filterList([estimate], searchText, ['memberName', 'projectName', 'expenditureTypeName', 'remunerationType', 'status']).length > 0;
    }), [estimates, searchText]);

    const handleFilterChange = (setter) => (event) => {
        setter(event.target.value);
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setSearchText('');
        setFilterProject('');
        setFilterExpenditureType('');
        setFilterMember('');
        setFilterStatus('');
        setCurrentPage(1);
    };

    useEffect(() => {
        fetchDropdownData();
        fetchData();
    }, [currentPage, filterProject, filterExpenditureType, filterMember, filterStatus]);

    const fetchDropdownData = async () => {
        try {
            const [projectsRes, expTypesRes, membersRes] = await Promise.all([
                ProjectService.getAll(1, 100), // Get all projects for dropdown
                ExpenditureTypeService.getAll(1, 100), // Get all expenditure types
                MemberService.getAll(1, 100) // Get all members
            ]);
            setProjects(projectsRes.data || []);
            setExpenditureTypes(expTypesRes.data || []);
            setMembers(membersRes.data || []);
        } catch (err) {
            utility.showError('Failed to load dropdown data: ' + err.message);
        }
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const data = await ProjectEstimatesService.getAll(currentPage, itemsPerPage, {
                projectId: filterProject,
                expenditureTypeId: filterExpenditureType,
                memberId: filterMember,
                status: filterStatus
            });
            setEstimates(data.data || []);
            setPageCount(data.pagination.totalPages || 0);
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (estimate = null) => {
        if (estimate) {
            setEditMode(true);
            setFormData({
                id: estimate.id,
                projectId: estimate.projectId,
                expenditureTypeId: estimate.expenditureTypeId,
                memberId: estimate.memberId || '',
                days: estimate.days || '',
                remunerationType: estimate.remunerationType || '',
                remunerationAmount: estimate.remunerationAmount || '',
                totalAmount: estimate.totalAmount || '',
                status: estimate.status
            });
        } else {
            setEditMode(false);
            setFormData({
                id: '',
                projectId: '',
                expenditureTypeId: '',
                memberId: '',
                days: '',
                remunerationType: '',
                remunerationAmount: '',
                totalAmount: '',
                status: 'active'
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({
            id: '',
            projectId: '',
            expenditureTypeId: '',
            memberId: '',
            days: '',
            remunerationType: '',
            remunerationAmount: '',
            totalAmount: '',
            status: 'active'
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            if (editMode) {
                await ProjectEstimatesService.update(formData.id, formData);
                utility.showSuccess('Project estimate updated successfully');
            } else {
                await ProjectEstimatesService.create(formData);
                utility.showSuccess('Project estimate created successfully');
            }
            handleCloseModal();
            fetchData();
        } catch (err) {
            utility.showError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handlePageClick = (event) => {
        setCurrentPage(event.selected + 1);
    };


    return (
        <div className="container-fluid py-2">
            <div className="row">
                <div className="col-12 text-end mb-3">
                    <button className="btn bg-gradient-dark mb-0" onClick={() => handleOpenModal()}>
                        <i className="material-symbols-rounded text-sm">add</i>&nbsp;&nbsp;Add New Estimate
                    </button>
                </div>
                <div className="col-12">
                    <div className="card my-4">
                        <div className="card-header p-0 position-relative mt-n4 mx-3 z-index-2">
                            <div className="bg-gradient-dark shadow-dark border-radius-lg pt-4 pb-3">
                                <h6 className="text-white text-capitalize ps-3">Project Estimates</h6>
                            </div>
                        </div>
                        <div className="card-body px-0 pb-2">
                            <div className="px-3 mb-3">
                                <div className="bg-white rounded-3 shadow-sm p-3">
                                    <div className="row gy-2 gx-2 align-items-end">
                                        <div className="col-md-4">
                                            <FilterBar
                                                value={searchText}
                                                onChange={setSearchText}
                                                onClear={() => setSearchText('')}
                                                placeholder="Search project estimates..."
                                            />
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label text-muted small">Project</label>
                                            <select className="form-select" value={filterProject} onChange={handleFilterChange(setFilterProject)}>
                                                <option value="">All Projects</option>
                                                {projects.map((project) => (
                                                    <option key={project.id} value={project.id}>{project.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label text-muted small">Expenditure Type</label>
                                            <select className="form-select" value={filterExpenditureType} onChange={handleFilterChange(setFilterExpenditureType)}>
                                                <option value="">All Types</option>
                                                {expenditureTypes.map((type) => (
                                                    <option key={type.id} value={type.id}>{type.expenditureTypeName}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label text-muted small">Member</label>
                                            <select className="form-select" value={filterMember} onChange={handleFilterChange(setFilterMember)}>
                                                <option value="">All Members</option>
                                                {members.map((member) => (
                                                    <option key={member.id} value={member.id}>{member.memberName || member.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-2">
                                            <label className="form-label text-muted small">Status</label>
                                            <select className="form-select" value={filterStatus} onChange={handleFilterChange(setFilterStatus)}>
                                                <option value="">Any</option>
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </select>
                                        </div>
                                        <div className="col-lg-2 col-md-6">
                                            <label className="form-label text-muted small">&nbsp;</label>
                                            <button type="button" className="mt-1 btn btn-outline-secondary w-100" onClick={resetFilters}>
                                                Clear Filters
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="table-responsive p-0">
                                {loading ? (
                                    <div className="p-4 text-center">Loading...</div>
                                ) : (
                                    <table className="table align-items-center mb-0">
                                        <thead>
                                            <tr>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Member</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Expenditure Type</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Project</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Days</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Remuneration Type</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Amount</th>
                                                <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Total Amount</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Status</th>
                                                <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredEstimates.length === 0 ? (
                                                <tr><td colSpan="9" className="text-center py-4">No project estimates found.</td></tr>
                                            ) : (
                                                filteredEstimates.map((estimate) => (
                                                    <tr key={estimate.id}>
                                                        <td>
                                                            <div className="d-flex px-2 py-1">
                                                                <div className="d-flex flex-column justify-content-center">
                                                                    <h6 className="mb-0 text-sm">{estimate.memberName || 'Unknown'}</h6>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{estimate.expenditureTypeName || 'Unknown'}</p>
                                                        </td>
                                                        <td>
                                                            <p className="text-xs font-weight-bold mb-0">{estimate.projectName || 'N/A'}</p>
                                                        </td>
                                                        <td className="align-middle">
                                                            <p className="text-xs font-weight-bold mb-0">{estimate.days || 'N/A'}</p>
                                                        </td>
                                                        <td className="align-middle">
                                                            <p className="text-xs font-weight-bold mb-0">{estimate.remunerationType || 'N/A'}</p>
                                                        </td>
                                                        <td className="align-middle">
                                                            <p className="text-xs font-weight-bold mb-0">{estimate.remunerationAmount || 'N/A'}</p>
                                                        </td>
                                                        <td className="align-middle">
                                                            <p className="text-xs font-weight-bold mb-0">{estimate.totalAmount || 'N/A'}</p>
                                                        </td>
                                                        <td className="align-middle text-center text-sm">
                                                            <span
                                                                className={`badge badge-sm bg-gradient-${estimate.status === 'active' ? 'success' : 'secondary'}`}
                                                            >
                                                                {estimate.status}
                                                            </span>
                                                        </td>
                                                        <td className="align-middle text-center">
                                                            <button className="btn btn-link text-info mb-0" onClick={() => handleOpenModal(estimate)}>
                                                                Edit
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colSpan="9" className="text-center">
                                                    <ReactPaginate
                                                        previousLabel="<"
                                                        nextLabel=">"
                                                        breakLabel="..."
                                                        pageCount={pageCount}
                                                        marginPagesDisplayed={2}
                                                        pageRangeDisplayed={5}
                                                        onPageChange={handlePageClick}
                                                        forcePage={currentPage - 1}
                                                        containerClassName="pagination"
                                                        pageClassName="page-item"
                                                        pageLinkClassName="page-link"
                                                        previousClassName="page-item"
                                                        previousLinkClassName="page-link"
                                                        nextClassName="page-item"
                                                        nextLinkClassName="page-link"
                                                        breakClassName="page-item"
                                                        breakLinkClassName="page-link"
                                                        activeClassName="active"
                                                        disabledClassName="disabled"
                                                    />
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>)}
                            </div>


                        </div>
                    </div>
                </div>

                {/* Modal */}
                {showModal && (
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                            <div className="modal-content">
                                <div className="modal-header border-0">
                                    <h5 className="modal-title font-weight-normal">
                                        {editMode ? 'Edit Project Estimate' : 'Add Project Estimate'}
                                    </h5>
                                    <button type="button" className="btn-close text-dark" aria-label="Close" onClick={handleCloseModal} />
                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div className="modal-body row">
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Project *</label>
                                                <select
                                                    name="projectId"
                                                    className="form-control"
                                                    value={formData.projectId}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select Project</option>
                                                    {projects.map(project => (
                                                        <option key={project.id} value={project.id}>
                                                            {project.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Expenditure Type *</label>
                                                <select
                                                    name="expenditureTypeId"
                                                    className="form-control"
                                                    value={formData.expenditureTypeId}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select Expenditure Type</option>
                                                    {expenditureTypes.map(type => (
                                                        <option key={type.id} value={type.id}>
                                                            {type.expenditureTypeName || type.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Member</label>
                                                <select
                                                    name="memberId"
                                                    className="form-control"
                                                    value={formData.memberId}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="">Select Member (Optional)</option>
                                                    {members.map(member => (
                                                        <option key={member.id} value={member.id}>
                                                            {member.memberName}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Days</label>
                                                <input
                                                    type="number"
                                                    name="days"
                                                    className="form-control"
                                                    value={formData.days}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter days"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Remuneration Type</label>
                                                <select
                                                    name="remunerationType"
                                                    className="form-control"
                                                    value={formData.remunerationType}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="">Select Type</option>
                                                    <option value="Daily">Daily</option>
                                                    <option value="Monthly">Monthly</option>
                                                    <option value="Fixed">Fixed</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Remuneration Amount</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    name="remunerationAmount"
                                                    className="form-control"
                                                    value={formData.remunerationAmount}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter amount"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Total Amount</label>
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    name="totalAmount"
                                                    className="form-control"
                                                    value={formData.totalAmount}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter total amount"
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-6 mb-4">
                                            <div className="input-group input-group-static">
                                                <label>Status</label>
                                                <select
                                                    name="status"
                                                    className="form-control"
                                                    value={formData.status}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="active">Active</option>
                                                    <option value="inactive">Inactive</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-footer border-0">
                                        <button type="button" className="btn bg-gradient-secondary" onClick={handleCloseModal} disabled={saving}>Cancel</button>
                                        <button type="submit" className="btn bg-gradient-dark" disabled={saving}>
                                            {saving ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    {editMode ? 'Updating...' : 'Saving...'}
                                                </>
                                            ) : (
                                                editMode ? 'Update' : 'Save'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
};

export default ProjectEstimates;